import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import cn from 'classnames';
import A_Button from '@/components/A_Atoms/A_Button/A_Button';
import A_ChatAvatar from '@/components/A_Atoms/A_ChatAvatar/A_ChatAvatar';
import O_HeaderResponsive from '@/components/O_Organisms/O_Header/O_HeaderResponsive';
import { ChatInvitePreview, chatApi } from '@/services/chatApi';
import { PATHS } from '@/constants/paths';
import { getTranslation } from '@/constants/translations';
import { useLanguage } from '@/contexts/LanguageContext';
import { displayName } from '@/utils/chat';
import styles from './ChatInvitePage.module.css';

/** Экран по пригласительной ссылке: что за чат и кнопка «вступить». */
export default function ChatInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = useCallback(
    (key: string) => getTranslation(language, key),
    [language]
  );

  const [preview, setPreview] = useState<ChatInvitePreview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;

    let isActive = true;
    chatApi
      .getInvitePreview(token)
      .then(response => {
        if (isActive) setPreview(response.data || null);
      })
      .catch(() => {
        if (isActive) setError(t('chat.invite.invalid'));
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const join = useCallback(async () => {
    if (!token) return;

    setIsJoining(true);
    setError(null);
    try {
      const response = await chatApi.acceptInvite(token);
      const conversation = response.data?.conversation;
      if (conversation) {
        navigate(PATHS.CHAT_CONVERSATION_BY_ID(conversation.id), {
          replace: true,
        });
      }
    } catch {
      setError(t('chat.invite.invalid'));
    } finally {
      setIsJoining(false);
    }
  }, [navigate, t, token]);

  return (
    <div className={styles.wrapper}>
      <O_HeaderResponsive showSearchInput={false} />
      <main className={styles.content}>
        <div className={styles.card}>
          {isLoading ? (
            <p className={cn(styles.caption, 'text-p-7')}>
              {t('chat.invite.loading')}
            </p>
          ) : preview ? (
            <>
              <A_ChatAvatar title={preview.title} isGroup size={72} />
              <h4 className={styles.title}>{preview.title}</h4>
              <p className={cn(styles.caption, 'text-p-7')}>
                {preview.memberCount} {t('chat.details.membersSuffix')}
                {preview.invitedBy
                  ? ` · ${t('chat.invite.invitedBy')} ${displayName(preview.invitedBy)}`
                  : ''}
              </p>
              <A_Button
                variant='primary'
                size='small'
                fontSize='text-p-4'
                width='100%'
                disabled={isJoining}
                onClick={
                  preview.isMember
                    ? () =>
                        navigate(
                          PATHS.CHAT_CONVERSATION_BY_ID(preview.conversationId)
                        )
                    : join
                }
              >
                {preview.isMember
                  ? t('chat.invite.open')
                  : t('chat.invite.join')}
              </A_Button>
            </>
          ) : (
            <p className={cn(styles.caption, 'text-p-7')}>
              {error || t('chat.invite.invalid')}
            </p>
          )}

          {error && preview && (
            <p className={cn(styles.error, 'text-caption')}>{error}</p>
          )}
        </div>
      </main>
    </div>
  );
}
